import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { QueryConfig } from '@/lib/tanstack-query';
import { Participation } from '@/types/api/participation';
import z from 'zod';

const GetParticipationIncludeSchema = z.enum(['products', 'user']);

export const GetParticipationParamsSchema = z.object({
  include: z.array(GetParticipationIncludeSchema).optional(),
});

export type GetParticipationParams = z.infer<typeof GetParticipationParamsSchema>;

export const getParticipation = async ({
  userId,
  saleId,
  params,
}: {
  userId: string;
  saleId: string;
  params?: GetParticipationParams;
}): Promise<Participation> => {
  return (await api.get(`/sales/${saleId}/participations/${userId}`, { params })).data;
};

export const getParticipationQueryOptions = (saleId: string, userId: string, params: GetParticipationParams = {}) => {
  return queryOptions({
    queryKey: ['sales', saleId, 'participations', userId, params],
    queryFn: () => getParticipation({ userId, saleId, params }),
    enabled: Boolean(userId) && Boolean(saleId),
  });
};

type UseParticipationOptions = {
  userId: string;
  saleId: string;
  params?: GetParticipationParams;
  queryConfig?: QueryConfig<typeof getParticipationQueryOptions>;
};

export const useParticipation = ({ saleId, userId, queryConfig, params }: UseParticipationOptions) => {
  return useQuery({
    ...getParticipationQueryOptions(saleId, userId, params),
    ...queryConfig,
  });
};

export const useParticipationSuspense = ({ saleId, userId, queryConfig, params }: UseParticipationOptions) => {
  return useSuspenseQuery({
    ...getParticipationQueryOptions(saleId, userId, params),
    ...queryConfig,
  });
};
